import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import portalService from '../../services/portalService';
import { useLanguage, t } from '../../context/LanguageContext';

const STATUS_CONFIG = {
  pending:     { color: '#ffc107', bg: '#fff8e1', label: { en: 'Pending',     es: 'Pendiente'   } },
  in_progress: { color: '#0d6efd', bg: '#e8f0fe', label: { en: 'In Progress', es: 'En Progreso' } },
  passed:      { color: '#198754', bg: '#e9f7ef', label: { en: 'Passed',      es: 'Aprobado'    } },
  failed:      { color: '#dc3545', bg: '#fdecea', label: { en: 'Failed',      es: 'Reprobado'   } },
  expired:     { color: '#6c757d', bg: '#f1f1f1', label: { en: 'Expired',     es: 'Vencido'     } },
};

const CATEGORY_LABELS = {
  food_safety:  { en: 'Food Safety',   es: 'Inocuidad' },
  gmp:          { en: 'GMP',           es: 'BPM'       },
  allergen:     { en: 'Allergen',      es: 'Alérgenos' },
  sanitation:   { en: 'Sanitation',    es: 'Saneamiento' },
  pest_control: { en: 'Pest Control',  es: 'Plagas'    },
  traceability: { en: 'Traceability',  es: 'Trazabilidad' },
  emergency:    { en: 'Emergency',     es: 'Emergencias' },
  other:        { en: 'Other',         es: 'Otro'      },
};

function fmt(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString();
}

export default function PortalTrainingPage() {
  const { lang } = useLanguage();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalService.getTrainingAssignments()
      .then(setAssignments)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="text-center py-5 text-muted">
        <div className="spinner-border spinner-border-sm me-2" />
        {t('Loading…', 'Cargando…', lang)}
      </div>
    );
  }

  const active   = assignments.filter(a => ['pending', 'in_progress', 'failed'].includes(a.status));
  const done     = assignments.filter(a => ['passed', 'expired'].includes(a.status));

  return (
    <div>
      <h5 className="fw-bold mb-3">📚 {t('My Trainings', 'Mis Capacitaciones', lang)}</h5>

      {assignments.length === 0 && (
        <div className="text-center py-5 text-muted">
          <div style={{ fontSize: 48 }}>📚</div>
          <p className="mt-2">{t('No trainings assigned yet.', 'Aún no tienes capacitaciones asignadas.', lang)}</p>
        </div>
      )}

      {active.length > 0 && (
        <>
          <div className="text-muted mb-2" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('To Complete', 'Por Completar', lang)}
          </div>
          {active.map(a => <AssignmentCard key={a.id} a={a} lang={lang} />)}
        </>
      )}

      {done.length > 0 && (
        <div className="mt-3">
          <div className="text-muted mb-2" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('Completed', 'Completadas', lang)}
          </div>
          {done.map(a => <AssignmentCard key={a.id} a={a} lang={lang} />)}
        </div>
      )}
    </div>
  );
}

function AssignmentCard({ a, lang }) {
  const cfg = STATUS_CONFIG[a.status] || STATUS_CONFIG.pending;
  const catLabel = CATEGORY_LABELS[a.Training?.category];
  const canStart = ['pending', 'in_progress', 'failed'].includes(a.status);
  const isOverdue = a.dueDate && a.status !== 'passed' && new Date(a.dueDate) < new Date();

  return (
    <div className="card mb-3 shadow-sm" style={{ borderLeft: `4px solid ${cfg.color}` }}>
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <div className="fw-semibold" style={{ fontSize: 15 }}>{a.Training?.title}</div>
            {catLabel && (
              <div className="text-muted" style={{ fontSize: 12 }}>{t(catLabel.en, catLabel.es, lang)}</div>
            )}
            <div className="d-flex gap-2 flex-wrap mt-1" style={{ fontSize: 12 }}>
              {a.Version?.estimatedMinutes && (
                <span className="text-muted">
                  ⏱ {a.Version.estimatedMinutes} {t('min', 'min', lang)}
                </span>
              )}
              {a.Version?.passingScore && (
                <span className="text-muted">
                  🎯 {a.Version.passingScore}% {t('to pass', 'para aprobar', lang)}
                </span>
              )}
              {a.dueDate && (
                <span style={{ color: isOverdue ? '#dc3545' : '#6c757d' }}>
                  📅 {t('Due', 'Vence', lang)}: {fmt(a.dueDate)}
                  {isOverdue && ` ⚠️ ${t('Overdue', 'Vencido', lang)}`}
                </span>
              )}
            </div>
            {a.bestScore != null && (
              <div style={{ fontSize: 12, color: cfg.color, marginTop: 2 }}>
                {t('Best score', 'Mejor puntaje', lang)}: {a.bestScore}%
              </div>
            )}
          </div>
          <div className="text-end ms-2" style={{ flexShrink: 0 }}>
            <div
              className="badge mb-2"
              style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`, fontSize: 11 }}
            >
              {t(cfg.label.en, cfg.label.es, lang)}
            </div>
            {canStart && (
              <div>
                <Link
                  to={`/portal/training/${a.id}`}
                  className="btn btn-sm btn-primary d-block"
                  style={{ fontSize: 12 }}
                >
                  {a.status === 'failed'
                    ? t('Retry', 'Reintentar', lang)
                    : a.status === 'in_progress'
                    ? t('Continue', 'Continuar', lang)
                    : t('Start', 'Iniciar', lang)}
                </Link>
              </div>
            )}
            {a.status === 'passed' && a.Version?.allowReview && (
              <div>
                <Link
                  to={`/portal/training/${a.id}`}
                  className="btn btn-sm btn-outline-success d-block"
                  style={{ fontSize: 12 }}
                >
                  {t('Review', 'Revisar', lang)}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
